import { FieldValues, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LocationMarkerIcon } from '@heroicons/react/outline';
import {
  createLocation,
  location,
  updateLocation,
} from '../../../../api/location';
import PrimaryButton from '../../../../components/Buttons/PrimaryButton';
import InputPresentational from '../../../../components/Form/InputPresentational';
import TitlePage from '../../../../components/TitlePage';
import H1Styled from '../../../../components/Typography/H1Styled';
import { useAuth } from '../../../../contexts/Auth';
import useYupValidationResolver from '../../../../libs/YupValidationResolver';
import { locationValidator } from '../../../../validators/location';

export default function ModificaLocation() {
  const [{ token }] = useAuth();

  const navigate = useNavigate();
  const { id, locationId } = useParams();

  const {
    formState: { errors },
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm({ resolver: useYupValidationResolver(locationValidator) });

  const address = watch('address');
  const city = watch('city');

  const getLocationFromAddress = async () => {
    if (!address || !city) {
      toast.error(
        "Inserisci almeno l'indirizzo e la città per ottenere le coordinate"
      );
      return;
    }

    const query = `${address}, ${city}`;

    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
        query
      )}&format=json&countrycodes=it&limit=5`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.length > 0) {
        const result =
          data.find((loc: any) => loc.addresstype !== 'county') || data[0];
        const { lat, lon } = result;

        setValue('latitude', Number(Number(lat).toFixed(6)));
        setValue('longitude', Number(Number(lon).toFixed(6)));
        toast.success("Coordinate ottenute dall'indirizzo!");
      } else {
        toast.error('Indirizzo non trovato. Verifica i dati inseriti.');
      }
    } catch (error) {
      toast.error("Errore nella ricerca delle coordinate dall'indirizzo");
    }
  };

  if (locationId) {
    useQuery({
      queryKey: ['location', locationId],
      queryFn: () => location(locationId),
      onSuccess: (data) => reset(data.data.attributes),
    });
  }

  const { mutate, isLoading } = useMutation({
    mutationFn: ({ location }: any) => {
      if (locationId)
        return updateLocation(Number(locationId), location, token);
      return createLocation(location, token);
    },
    onSuccess: () => {
      toast.success('Location creata con successo');
      navigate(`/sezioni/modifica/${id}/location`);
    },
    onError: (error: any) => {
      toast.error(error.response.data.error.message);
    },
  });

  const createNewLocation = async (data: FieldValues) => {
    mutate({
      location: { ...data, sections: id },
    });
  };

  return (
    <>
      <TitlePage title='Modifica sezione | Location' />

      <form className='mt-6' onSubmit={handleSubmit(createNewLocation)}>
        <H1Styled>
          {locationId ? 'Modifica' : 'Aggiungi nuova'} Location
        </H1Styled>
        <div className='grid grid-cols-1 gap-4 pt-10 pb-8 md:grid-cols-2'>
          <InputPresentational
            id='name'
            label='Nome Location'
            name='name'
            type='text'
            register={register}
            required
            error={{ ...errors.name, type: 'required' }}
          />
          <InputPresentational
            id='city'
            label='Città'
            name='city'
            type='text'
            register={register}
            required
            error={{ ...errors.city, type: 'required' }}
          />
          <InputPresentational
            id='address'
            label='Indirizzo'
            name='address'
            type='text'
            register={register}
            required
            error={{ ...errors.address, type: 'required' }}
          />

          <InputPresentational
            id='website'
            label='Sito web'
            name='website'
            type='text'
            register={register}
          />
        </div>

        <div className='pb-8'>
          <div className='flex items-center justify-between mb-2'>
            <label className='block text-sm font-medium text-gray-700'>
              Coordinate GPS (*)
            </label>
            <button
              type='button'
              onClick={getLocationFromAddress}
              className='flex items-center px-3 py-1 text-xs font-medium text-primary hover:text-primary-dark'
            >
              <LocationMarkerIcon className='h-3 w-3 mr-1' />
              Ottieni da indirizzo
            </button>
          </div>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <InputPresentational
              id='latitude'
              label='Latitudine'
              name='latitude'
              type='text'
              placeholder='45.123456'
              register={register}
              required
              error={errors.latitude as any}
            />
            <InputPresentational
              id='longitude'
              label='Longitudine'
              name='longitude'
              type='text'
              placeholder='9.123456'
              register={register}
              required
              error={errors.longitude as any}
            />
          </div>
          <p className='mt-1 text-sm text-gray-500'>
            Le coordinate permettono agli eventi tenuti in questa location di
            essere cercati in base al luogo
          </p>
        </div>

        <div className='flex items-center h-10'>
          <div className='flex items-center justify-between space-x-6'>
            <PrimaryButton
              outline
              onClick={() => {
                navigate('/sezioni/modifica/' + id + '/location');
              }}
            >
              Indietro
            </PrimaryButton>
            <PrimaryButton type='submit'>Salva</PrimaryButton>
          </div>
        </div>
      </form>
    </>
  );
}
